import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/EmptyState';
import { StatusPill } from '../../components/StatusPill';
import { cn } from '../../lib/cn';
import { PageScaffold } from '../../components/PageScaffold';

interface CommissionBand {
  from: string;
  to: string | null;
  ratePct: string;
  slice: string;
  commission: string;
}

interface DistributionRow {
  userId: string;
  name: string;
  role: string;
  receivesShare: boolean;
  rawNet: string;
  fullNet: string;
  promoNet: string;
  weightedNet: string;
  share: string;
  orderCount: number;
}

interface DirectRow {
  kind: 'special' | 'online' | 'cross_sale';
  userId: string;
  userName: string;
  shopId: string;
  orderId: string;
  orderNumber: string;
  confirmedAt: string | null;
  label: string;
  productName: string;
  basis: string;
  pct: string;
  halved: boolean;
  commission: string;
  payable: boolean;
  pendingReason: string | null;
}

interface RollupRow {
  userId: string;
  name: string;
  role: string;
  poolShare: string;
  specialPayable: string;
  specialPending: string;
  online: string;
  crossSale: string;
  payableTotal: string;
  total: string;
}

interface CommissionData {
  period: { from: string; to: string };
  shops: Array<{
    shop: { id: string; code: string; name: string; type: string };
    orderCount: number;
    netSales: string;
    tierMode: 'marginal' | 'cliff';
    qualified: boolean;
    qualifyingMin: string;
    ratePct: string;
    blendedRatePct: string;
    commission: string;
    distributed: string;
    targetMonthly: string | null;
    targetRatio: number | null;
    bands: CommissionBand[];
    distribution: DistributionRow[];
  }>;
  direct: DirectRow[];
  perUser: RollupRow[];
}

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 days' },
  { key: 'month', label: 'Month' },
  { key: 'ytd', label: 'YTD' },
];

function pct(v: string): string {
  return `${Number((Number(v) * 100).toFixed(4))}%`;
}

export function CommissionPage(): JSX.Element {
  const { perms } = useAuth();
  // Company-wide history is owner/finance only; a sales girl viewing this page
  // sees only her own current-period numbers and uses /my-commission for history.
  const canSeeAll = perms.has('finance.view_all_commissions');
  const [period, setPeriod] = useState('month');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const data = useQuery({
    queryKey: ['commission', period],
    queryFn: () => apiGet<CommissionData>('/finance/commission', { period }),
  });

  const shops = data.data?.shops ?? [];
  const totalPool = shops.reduce((s, x) => s + Number(x.commission), 0);
  const totalNet = shops.reduce((s, x) => s + Number(x.netSales), 0);
  const totalDirect = (data.data?.direct ?? []).reduce((s, x) => s + Number(x.commission), 0);
  const totalPaidOut = (data.data?.perUser ?? []).reduce((s, x) => s + Number(x.total), 0);

  return (
    <PageScaffold
      title="Commission"
      subtitle="Each shop's barem unlocks a pool on its monthly net; the pool is shared pro-rata by each girl's own sales (promo / discounted lines at half weight). Special models pay direct once paid + delivered; online sales pay per-category rates. When a shop misses its target the pool stays locked, but each girl still earns 5% on her eligible cross-sales accessories — but only on invoices that also include a mattress."
    >

      <Card bodyClassName="p-4">
        <div className="flex flex-wrap gap-1.5 items-center">
          <span className="text-xs uppercase tracking-wide text-ink-3 mr-2">Period</span>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={cn(
                'px-3 py-1.5 rounded text-xs uppercase tracking-wide transition border',
                period === p.key ? 'bg-accent text-white border-accent' : 'bg-paper-2 text-ink-2 border-rule hover:border-accent/40',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </Card>

      {data.isLoading || !data.data ? (
        <div className="text-center text-ink-3 py-12">Loading…</div>
      ) : shops.length === 0 && data.data.direct.length === 0 ? (
        <Card><EmptyState title="No shops" /></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Hero
              label="Commission accrued"
              value={<Money value={totalPaidOut} className="text-accent" />}
              hint="pool shares + direct (incl. pending)"
            />
            <Hero
              label="Shop pools unlocked"
              value={<Money value={totalPool} />}
              hint={`across ${shops.length} shop${shops.length === 1 ? '' : 's'}`}
            />
            <Hero
              label="Direct commissions"
              value={<Money value={totalDirect} />}
              hint="special models + online + cross-sales"
            />
            <Hero
              label="Pool basis (net)"
              value={<Money value={totalNet} />}
              hint="net of discounts/VAT, excl. special + online"
            />
          </div>

          {/* Who earns what */}
          <Card title="Who earns what" subtitle="Pool shares pro-rata of each girl's sales per shop + direct entries" bodyClassName="p-0">
            {data.data.perUser.length === 0 ? (
              <EmptyState title="No commission earned yet this period" />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                    <th className="px-6 py-2 font-medium">Salesperson</th>
                    <th className="px-3 py-2 font-medium text-right">Pool share</th>
                    <th className="px-3 py-2 font-medium text-right">Online</th>
                    <th className="px-3 py-2 font-medium text-right">Cross-sales</th>
                    <th className="px-3 py-2 font-medium text-right">Special (payable)</th>
                    <th className="px-3 py-2 font-medium text-right">Special (pending)</th>
                    <th className="px-6 py-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.perUser.map((u) => (
                    <tr key={u.userId} className="border-b border-rule-2 hover:bg-paper-2">
                      <td className="px-6 py-3 text-ink">{u.name}</td>
                      <td className="px-3 py-3 text-right"><Money value={u.poolShare} /></td>
                      <td className="px-3 py-3 text-right"><Money value={u.online} muted /></td>
                      <td className="px-3 py-3 text-right">
                        {Number(u.crossSale) > 0 ? <Money value={u.crossSale} muted /> : <span className="text-ink-4">—</span>}
                      </td>
                      <td className="px-3 py-3 text-right"><Money value={u.specialPayable} muted /></td>
                      <td className="px-3 py-3 text-right">
                        {Number(u.specialPending) > 0 ? (
                          <span className="text-warning"><Money value={u.specialPending} /></span>
                        ) : (
                          <span className="text-ink-4">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right"><Money value={u.total} className="font-medium text-accent" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title="Per shop" subtitle="Tap a shop to see its pool distribution and band breakdown" bodyClassName="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-6 py-2 font-medium">Shop</th>
                  <th className="px-3 py-2 font-medium text-right">Orders</th>
                  <th className="px-3 py-2 font-medium text-right">Net sales</th>
                  <th className="px-3 py-2 font-medium text-right">Eff. rate</th>
                  <th className="px-3 py-2 font-medium text-right">Pool</th>
                  <th className="px-6 py-2 font-medium text-right">Distributed</th>
                </tr>
              </thead>
              <tbody>
                {shops.map((s) => {
                  const isOpen = open.has(s.shop.id);
                  const expandable = s.bands.length > 0 || s.distribution.length > 0;
                  return (
                    <Fragment key={s.shop.id}>
                      <tr
                        className={cn('border-b border-rule-2 hover:bg-paper-2', expandable && 'cursor-pointer')}
                        onClick={() => expandable && toggle(s.shop.id)}
                      >
                        <td className="px-6 py-3">
                          {expandable && <span className="text-ink-3 mr-1.5 inline-block w-3">{isOpen ? '▾' : '▸'}</span>}
                          <span className="kc-mono text-xs text-ink-3 mr-2">{s.shop.code}</span>
                          <span className="text-ink">{s.shop.name}</span>
                        </td>
                        <td className="px-3 py-3 text-right kc-mono">{s.orderCount}</td>
                        <td className="px-3 py-3 text-right"><Money value={s.netSales} /></td>
                        <td className="px-3 py-3 text-right kc-mono" title="Blended rate (pool ÷ net)">
                          {(Number(s.blendedRatePct) * 100).toFixed(2)}%
                        </td>
                        <td className="px-3 py-3 text-right">
                          <Money value={s.commission} className="font-medium" />
                          {!s.qualified && Number(s.qualifyingMin) > 0 && (
                            <div className="text-[10px] text-warning">below Rs {Number(s.qualifyingMin).toLocaleString()} min</div>
                          )}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <Money value={s.distributed} className="font-medium text-accent" />
                          {Number(s.distributed) < Number(s.commission) && (
                            <div className="text-[10px] text-ink-4" title="Promo halving + non-sales_girl sales stay with the house">
                              of <Money value={s.commission} muted />
                            </div>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-paper-2/60 border-b border-rule-2">
                          <td colSpan={6} className="px-6 py-4 space-y-4">
                            {s.distribution.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold mb-1.5">
                                  Pool distribution — pro-rata of each girl's sales
                                </div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-left text-ink-3">
                                      <th className="py-1 font-medium">Salesperson</th>
                                      <th className="py-1 font-medium text-right">Orders</th>
                                      <th className="py-1 font-medium text-right">Sales</th>
                                      <th className="py-1 font-medium text-right">of which promo/disc.</th>
                                      <th className="py-1 font-medium text-right">Counted (½ promo)</th>
                                      <th className="py-1 font-medium text-right">Share</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {s.distribution.map((d) => (
                                      <tr key={d.userId}>
                                        <td className="py-1 text-ink-2">
                                          {d.name}
                                          {!d.receivesShare && (
                                            <span className="ml-1.5 text-[10px] text-ink-4" title="Only sales girls receive a pool share">
                                              ({d.role} — no share)
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-1 text-right kc-mono">{d.orderCount}</td>
                                        <td className="py-1 text-right"><Money value={d.rawNet} muted /></td>
                                        <td className="py-1 text-right">
                                          {Number(d.promoNet) > 0 ? <Money value={d.promoNet} muted /> : <span className="text-ink-4">—</span>}
                                        </td>
                                        <td className="py-1 text-right"><Money value={d.weightedNet} muted /></td>
                                        <td className="py-1 text-right"><Money value={d.share} className={d.receivesShare ? 'text-accent' : undefined} /></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {s.bands.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold mb-1.5">
                                  Barem bands on shop net
                                </div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-left text-ink-3">
                                      <th className="py-1 font-medium">Band</th>
                                      <th className="py-1 font-medium text-right">Rate</th>
                                      <th className="py-1 font-medium text-right">Slice</th>
                                      <th className="py-1 font-medium text-right">Earns</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {s.bands.map((b, i) => (
                                      <tr key={i}>
                                        <td className="py-1 kc-mono text-ink-2">
                                          {Number(b.from).toLocaleString()} → {b.to === null ? '∞' : Number(b.to).toLocaleString()}
                                        </td>
                                        <td className="py-1 text-right kc-mono">{(Number(b.ratePct) * 100).toFixed(2)}%</td>
                                        <td className="py-1 text-right"><Money value={b.slice} muted /></td>
                                        <td className="py-1 text-right"><Money value={b.commission} /></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {/* Direct commissions — special models + online */}
          <Card
            title="Direct commissions"
            subtitle="Special models (paid on full payment + delivery), online-channel sales, and cross-sales accessories (5% floor when a shop misses target) — never part of a shop pool"
            bodyClassName="p-0"
          >
            {data.data.direct.length === 0 ? (
              <EmptyState title="No direct commission entries this period" />
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                    <th className="px-6 py-2 font-medium">Order</th>
                    <th className="px-3 py-2 font-medium">Salesperson</th>
                    <th className="px-3 py-2 font-medium">What</th>
                    <th className="px-3 py-2 font-medium text-right">Basis</th>
                    <th className="px-3 py-2 font-medium text-right">Rate</th>
                    <th className="px-3 py-2 font-medium text-right">Commission</th>
                    <th className="px-6 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.direct.map((d, i) => (
                    <tr key={`${d.orderId}-${i}`} className="border-b border-rule-2 hover:bg-paper-2">
                      <td className="px-6 py-2.5">
                        <span className="kc-mono text-xs text-ink-2">{d.orderNumber}</span>
                        {d.confirmedAt && (
                          <div className="text-[10px] text-ink-4">{new Date(d.confirmedAt).toLocaleDateString('en-MU', { day: 'numeric', month: 'short' })}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-ink-2">{d.userName}</td>
                      <td className="px-3 py-2.5">
                        <StatusPill tone={d.kind === 'special' ? 'accent' : d.kind === 'cross_sale' ? 'warning' : 'info'}>
                          {d.kind === 'cross_sale' ? 'cross-sale' : d.kind}
                        </StatusPill>
                        <span className="ml-2 text-xs text-ink-2">{d.label}</span>
                        <div className="text-[10px] text-ink-4 truncate max-w-[220px]">{d.productName}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right"><Money value={d.basis} muted /></td>
                      <td className="px-3 py-2.5 text-right kc-mono text-xs">
                        {pct(d.pct)}
                        {d.halved && <span className="text-warning" title="Half rate — promo/discounted sale"> ×½</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right"><Money value={d.commission} className="font-medium" /></td>
                      <td className="px-6 py-2.5">
                        {d.payable ? (
                          <StatusPill tone="positive">payable</StatusPill>
                        ) : (
                          <StatusPill tone="warning">awaiting {d.pendingReason}</StatusPill>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      {/* Month-by-month history of commission owed — independent of the period
          selector above. Owner / finance only. */}
      {canSeeAll && <MonthlyHistory />}
    </PageScaffold>
  );
}

// ── Monthly history — what was owed each month, with a per-person split ───────

interface HistoryUser {
  userId: string;
  name: string;
  poolShare: string;
  direct: string;
  payable: string;
  total: string;
}
interface HistoryMonth {
  month: string;
  poolShare: string;
  direct: string;
  payable: string;
  total: string;
  perUser: HistoryUser[];
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, 1)).toLocaleDateString('en-MU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function MonthlyHistory(): JSX.Element {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (k: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const q = useQuery({
    queryKey: ['commission-history'],
    queryFn: () => apiGet<{ months: HistoryMonth[] }>('/finance/commission/history', { months: '12' }),
  });
  const months = q.data?.months ?? [];

  return (
    <Card
      title="Monthly history"
      subtitle="Commission owed each month (pool shares + direct). Recomputed live — a record of what was due, not a payout ledger. Tap a month for the per-person split."
      bodyClassName="p-0"
    >
      {q.isLoading ? (
        <div className="text-center text-ink-3 py-8">Loading history…</div>
      ) : months.every((m) => Number(m.total) === 0) ? (
        <EmptyState title="No commission history yet" description="Months with commission appear here once orders are confirmed." />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
              <th className="px-6 py-2 font-medium">Month</th>
              <th className="px-3 py-2 font-medium text-right">Pool shares</th>
              <th className="px-3 py-2 font-medium text-right">Direct</th>
              <th className="px-3 py-2 font-medium text-right" title="Excludes special-model commission still awaiting full payment + delivery">
                Payable now
              </th>
              <th className="px-6 py-2 font-medium text-right">Total owed</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const isOpen = open.has(m.month);
              const expandable = m.perUser.length > 0;
              return (
                <Fragment key={m.month}>
                  <tr
                    className={cn('border-b border-rule-2 hover:bg-paper-2', expandable && 'cursor-pointer')}
                    onClick={() => expandable && toggle(m.month)}
                  >
                    <td className="px-6 py-3">
                      {expandable && (
                        <span className="text-ink-3 mr-1.5 inline-block w-3">{isOpen ? '▾' : '▸'}</span>
                      )}
                      <span className="text-ink">{monthLabel(m.month)}</span>
                    </td>
                    <td className="px-3 py-3 text-right"><Money value={m.poolShare} muted /></td>
                    <td className="px-3 py-3 text-right"><Money value={m.direct} muted /></td>
                    <td className="px-3 py-3 text-right"><Money value={m.payable} /></td>
                    <td className="px-6 py-3 text-right">
                      <Money value={m.total} className="font-medium text-accent" />
                      {Number(m.payable) < Number(m.total) && (
                        <div className="text-[10px] text-warning" title="Difference is special-model commission still awaiting payment + delivery">
                          <Money value={String(Number(m.total) - Number(m.payable))} muted /> pending
                        </div>
                      )}
                    </td>
                  </tr>
                  {isOpen && expandable && (
                    <tr className="bg-paper-2/60 border-b border-rule-2">
                      <td colSpan={5} className="px-6 py-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-ink-3">
                              <th className="py-1 font-medium">Salesperson</th>
                              <th className="py-1 font-medium text-right">Pool</th>
                              <th className="py-1 font-medium text-right">Direct</th>
                              <th className="py-1 font-medium text-right">Payable now</th>
                              <th className="py-1 font-medium text-right">Total owed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {m.perUser.map((u) => (
                              <tr key={u.userId}>
                                <td className="py-1 text-ink-2">{u.name}</td>
                                <td className="py-1 text-right"><Money value={u.poolShare} muted /></td>
                                <td className="py-1 text-right"><Money value={u.direct} muted /></td>
                                <td className="py-1 text-right"><Money value={u.payable} muted /></td>
                                <td className="py-1 text-right"><Money value={u.total} className="text-accent" /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function Hero({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }): JSX.Element {
  return (
    <Card>
      <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">{label}</div>
      <div className="text-3xl font-medium kc-serif text-ink mt-2">{value}</div>
      {hint && <div className="text-xs text-ink-3 mt-2">{hint}</div>}
    </Card>
  );
}
